import React, { useState } from 'react';
import { 
    ChartBarIcon,
    EyeIcon,
    ArrowLeftIcon,
    ArrowDownTrayIcon,
    CheckIcon,
    XMarkIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import CvPreview from './CvPreview';

const AbTesting = ({ versionA, versionB, onBack, onExport, isLoading }) => {
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [testResults, setTestResults] = useState({
        versionA: { score: 0, feedback: '' },
        versionB: { score: 0, feedback: '' }
    });
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [winner, setWinner] = useState(null);

    const analyzeVersion = async (version, versionKey) => {
        setIsAnalyzing(true);
        try {
            // Simulation d'analyse IA
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const mockScore = Math.floor(Math.random() * 40) + 60; // Score entre 60-100
            const mockFeedback = generateMockFeedback(mockScore);
            
            setTestResults(prev => ({
                ...prev,
                [versionKey]: { score: mockScore, feedback: mockFeedback }
            }));
            
            return mockScore;
        } catch (error) {
            // Erreur silencieuse pour l'analyse
        } finally {
            setIsAnalyzing(false);
        }
    };

    const generateMockFeedback = (score) => {
        if (score >= 90) {
            return "Excellent CV ! La structure est parfaite, les mots-clés sont bien placés et le design est professionnel.";
        } else if (score >= 80) {
            return "Très bon CV. Quelques améliorations mineures possibles sur la mise en forme.";
        } else if (score >= 70) {
            return "Bon CV avec des points forts. Recommandations pour améliorer l'impact.";
        } else {
            return "CV correct mais des améliorations importantes sont nécessaires pour optimiser l'impact.";
        }
    };

    const runAbTest = async () => {
        setIsAnalyzing(true);
        try {
            const scoreA = await analyzeVersion(versionA, 'versionA');
            const scoreB = await analyzeVersion(versionB, 'versionB');
            
            // Déterminer le gagnant
            if (scoreA > scoreB) {
                setWinner('A');
            } else if (scoreB > scoreA) {
                setWinner('B');
            } else {
                setWinner('tie');
            }
        } catch (error) {
            // Erreur silencieuse pour le test A/B
        }
    };

    const getScoreColor = (score) => {
        if (score >= 90) return 'text-green-600 bg-green-100';
        if (score >= 80) return 'text-blue-600 bg-blue-100';
        if (score >= 70) return 'text-yellow-600 bg-yellow-100';
        return 'text-red-600 bg-red-100';
    };

    const getScoreLabel = (score) => {
        if (score >= 90) return 'Excellent';
        if (score >= 80) return 'Très bon';
        if (score >= 70) return 'Bon';
        return 'À améliorer';
    };

    return (
        <div className="p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Test A/B de votre CV</h2>
                    <p className="text-gray-600">Comparez deux versions et découvrez laquelle est la plus efficace</p>
                </div>
                <button
                    onClick={onBack}
                    className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                    Retour
                </button>
            </div>

            {/* Test Setup */}
            {!selectedVersion && (
                <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                    <div className="text-center mb-8">
                        <ChartBarIcon className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">
                            Prêt pour le test A/B ?
                        </h3>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Notre IA va analyser les deux versions de votre CV et vous donner des recommandations 
                            pour optimiser votre impact auprès des recruteurs.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                        {/* Version A */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                            <div className="text-center mb-4">
                                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold text-lg">A</span>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-800">Version A</h4>
                                <p className="text-sm text-gray-600">Version actuelle</p>
                            </div>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>✓ Template : {versionA?.template?.name || 'Moderne'}</p>
                                <p>✓ Couleurs personnalisées</p>
                                <p>✓ Contenu complet</p>
                            </div>
                        </div>

                        {/* Version B */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                            <div className="text-center mb-4">
                                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold text-lg">B</span>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-800">Version B</h4>
                                <p className="text-sm text-gray-600">Version alternative</p>
                            </div>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>✓ Template différent</p>
                                <p>✓ Mise en page alternative</p>
                                <p>✓ Optimisations suggérées</p>
                            </div>
                        </div>
                    </div>

                    <div className="text-center">
                        <button
                            onClick={runAbTest}
                            disabled={isAnalyzing}
                            className="flex items-center mx-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
                        >
                            {isAnalyzing ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                    Analyse en cours...
                                </>
                            ) : (
                                <>
                                    <SparklesIcon className="w-5 h-5 mr-3" />
                                    Lancer l'analyse A/B
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Results */}
            {testResults.versionA.score > 0 && (
                <div className="space-y-8">
                    {/* Winner Announcement */}
                    {winner && (
                        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                            <div className="mb-4">
                                {winner === 'tie' ? (
                                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                                        <span className="text-2xl">🤝</span>
                                    </div>
                                ) : (
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                        <CheckIcon className="w-8 h-8 text-green-600" />
                                    </div>
                                )}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                {winner === 'tie' 
                                    ? 'Match nul !' 
                                    : `Version ${winner} gagnante !`
                                }
                            </h3>
                            <p className="text-gray-600">
                                {winner === 'tie' 
                                    ? 'Les deux versions sont équivalentes. Choisissez selon vos préférences.'
                                    : `La version ${winner} obtient le meilleur score d'optimisation.`
                                }
                            </p>
                        </div>
                    )}

                    {/* Comparison */}
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Version A Results */}
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <div className="text-center mb-6">
                                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold text-lg">A</span>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-800">Version A</h4>
                            </div>

                            <div className="text-center mb-6">
                                <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(testResults.versionA.score)}`}>
                                    {testResults.versionA.score}/100
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    {getScoreLabel(testResults.versionA.score)}
                                </p>
                            </div>

                            <div className="mb-6">
                                <h5 className="font-semibold text-gray-800 mb-3">Analyse IA :</h5>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {testResults.versionA.feedback}
                                </p>
                            </div>

                            <button
                                onClick={() => setSelectedVersion('A')}
                                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <EyeIcon className="w-4 h-4 mr-2" />
                                Voir la version
                            </button>
                        </div>

                        {/* Version B Results */}
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <div className="text-center mb-6">
                                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold text-lg">B</span>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-800">Version B</h4>
                            </div>

                            <div className="text-center mb-6">
                                <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(testResults.versionB.score)}`}>
                                    {testResults.versionB.score}/100
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    {getScoreLabel(testResults.versionB.score)}
                                </p>
                            </div>

                            <div className="mb-6">
                                <h5 className="font-semibold text-gray-800 mb-3">Analyse IA :</h5>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {testResults.versionB.feedback}
                                </p>
                            </div>

                            <button
                                onClick={() => setSelectedVersion('B')}
                                disabled={!versionB}
                                className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <EyeIcon className="w-4 h-4 mr-2" />
                                {versionB ? 'Voir la version' : 'Version non disponible'}
                            </button>
                        </div>
                    </div>

                    {/* Recommendations */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <SparklesIcon className="w-5 h-5 mr-2 text-blue-600" />
                            Recommandations d'optimisation
                        </h3>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Points forts :</h4>
                                <ul className="space-y-1 text-sm text-gray-600">
                                    <li>✓ Structure claire et professionnelle</li>
                                    <li>✓ Mots-clés pertinents</li>
                                    <li>✓ Design adapté au secteur</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Améliorations suggérées :</h4>
                                <ul className="space-y-1 text-sm text-gray-600">
                                    <li>• Optimiser les mots-clés ATS</li>
                                    <li>• Améliorer la hiérarchie visuelle</li>
                                    <li>• Ajouter des métriques quantifiables</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Export Actions */}
                    <div className="flex justify-center space-x-4">
                        <button
                            onClick={() => onExport('pdf')}
                            disabled={isLoading}
                            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                            Exporter la meilleure version
                        </button>
                    </div>
                </div>
            )}

            {/* Version Preview Modal */}
            {selectedVersion && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-800">
                                Version {selectedVersion} - Aperçu
                            </h3>
                            <button
                                onClick={() => setSelectedVersion(null)}
                                className="p-2 text-gray-500 hover:text-gray-700"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                            {selectedVersion === 'A' ? (
                                <CvPreview
                                    cvData={versionA?.cvData}
                                    cvStyle={versionA?.cvStyle}
                                    template={versionA?.template}
                                    isEmbedded={true}
                                />
                            ) : (
                                <CvPreview
                                    cvData={versionB?.cvData}
                                    cvStyle={versionB?.cvStyle}
                                    template={versionB?.template}
                                    isEmbedded={true}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AbTesting; 